import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { useSelector } from "react-redux";

const WaterfallChartComponent = () => {
  const pid = useSelector((state) => state.user.projectId);

  const [orgOptions, setOrgOptions] = useState([]);
  const [schemeOptions, setSchemeOptions] = useState([]);
  const [schemeTypeOptions, setSchemeTypeOptions] = useState([]);
  const [caseDistrictOptions,setCaseDistrictOptions]=useState([]);
  const [thematicAreaOptions, setThematicAreaOptions] = useState([]);

  const [selectedOrgs, setSelectedOrgs] = useState([]);
  const [selectedSchemes, setSelectedSchemes] = useState([]);
  const [selectedSchemeTypes, setSelectedSchemeTypes] = useState([]);
  const [selectedCaseDistrict,setSelectedCaseDistrict]=useState([]);
  const [selectedThematicAreas, setSelectedThematicAreas] = useState([]);

  const [waterfallData, setWaterfallData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [metricsData, setMetricsData] = useState([]);
  const [schemeBenefit, setSchemeBenefit] = useState(0);
  
  // State for info popups
  const [infoPopup, setInfoPopup] = useState({ show: false, title: '', description: '', example: '' });

  // Definitions with examples for different elements
  const definitions = {
    "Total Applications Opened": {
      title: "Total Applications Opened",
      description: "Total number of applications that have been opened across all schemes and states. This includes all applications regardless of their current status.This includes aborted and rejected applications as well.",
      example: "Total Applications Opened = Valid Application Open + Applications Aborted + Applications Rejected\nExample: 100 = 90 + 7 + 3"
    },
    "Applications Opened": {
      title: "Applications Opened",
      description: "This shows the total count of Valid Applications.This does not include Applications Aborted and Applications Rejected Count .",
    },
    "Applications Submitted": {
      title: "Applications Submitted",
      description: "This shows the total count of applications in 'Submitted' status. These are applications where the proof of submission has been uploaded."
    },
    "Applications with Benefit Received": {
      title: "Applications with Benefit Received",
      description: "This shows the total count of applications in 'Benefit Received' status. These are applications where the proof of benefit has been uploaded on the app.",
    },
    "Applications Aborted": {
      title: "Applications Aborted",
      description: "These are applications that were started by HD but aborted either due to incorrect internal records or during internal quality checks.",
    },
    "Applications Rejected": {
      title: "Applications Rejected",
      description: "These are applications that were rejected by the government.",
    },
    "Current Open": {
      title: "Open Applications",
      description: "Displays the current count of Applications that are in Open status. These are applications that have been started but not yet submitted.",
      example: "Current Open = Applications Opened - Applications Submitted\n40 = 90 - 50"
    },
    "Current Submitted": {
      title: "Applications in Submitted status",
      description: "Shows the current number of applications in the Submitted stage that are waiting for benefit verification. These are applications where proof of submission has been uploaded but benefit is not yet received.",
      example: "Current Submitted = Applications Submitted - Applications with Benefit Received\n10 = 50 - 40"
    },
    "Current Benefit Received": {
      title: "Applications with Benefits Received",
      description: "Represents the cumulative count of applications that have successfully reached the Benefit Received stage. This provides an indicator of the actual outcomes and impact achieved so far.",
    },
    "Benefits unlocked worth": {
      title: "Benefits unlocked worth",
      description: "The total monetary value of benefits that have been unlocked and received by citizens.",
    },
    "Snapshot View": {
      title: "Snapshot View",
      description: "Snapshot represents view of applications based on the status they are in at this point in time i.e., how many applications are currently in Open status, how many are in Submitted status etc. Since application status change as work happens, the counts in snapshot view change every day, this is a near real time view of work progressing on ground.",
    },
    "Org/Workspace": {
      title: "Organization/Workspace",
      description: "Choose the Org or Workspace name, which corresponds to the state or workspace associated with the selected Project.",
      example: "Example: Maharashtra, Karnataka, Delhi NCR, etc."
    },
    "Service Type": {
      title: "Service Type",
      description: "Choose the service type associated with the selected Project to filter the data",
      example: "Example: Schemes, Document, DFL, Yojana Card, EDP"
    },
    "Service Name": {
      title: "Service Name",
      description: "Choose the Scheme Name. A filter used to display schemes associated with the selected Project to filter the data.",
      example: "Example: PM Awas Yojana, Ayushman Bharat, PM Kisan Samman Nidhi, etc."
    },
    "Service District": {
      title: "Service District",
      description: "Filter data by the district where services are being provided. This helps in analyzing location-specific performance.",
      example: "Example: Gurugram, Bangalore, Delhi, Chennai, etc."
    },
    "Service Category": {
      title: "Service Category",
      description: "Filter data by thematic area or service category to analyze specific program areas and their impact.",
      example: "Example: Housing, Healthcare, Agriculture, Education, etc."
    }
  };

  const getQueryParams = () => {
    const params = new URLSearchParams();
    selectedSchemeTypes.forEach(type => params.append("SchemeType", type));
    selectedOrgs.forEach(org => params.append("OrgName", org));
    selectedSchemes.forEach(scheme => params.append("SchemeName", scheme));
    selectedCaseDistrict.forEach(district => params.append("CaseDistrict", district));
    selectedThematicAreas.forEach(area => params.append("ThematicArea", area));
    if (pid) params.append("pid", pid);
    return params.toString();
  };

  const mergeOptions = (selected, fetched) => {
    const map = new Map();
    [...selected, ...fetched].forEach(opt => map.set(opt.value, opt));
    return Array.from(map.values());
  };

  const schemeTypeLabelMap = {
    sch: "Schemes",
    doc: "Document",
    dfl: "DFL",
    yc: "Yojana Card"
  };

  const fetchFilterOptions = async () => {
    try {
      const params = getQueryParams();

      const [orgRes, schemeRes, schemeTypeRes,caseDistrictRes,thematicRes] = await Promise.all([
        fetch(`https://api-project360-insights.haqdarshak.com/kpi/waterfall/unique-org/?${params}`),
        fetch(`https://api-project360-insights.haqdarshak.com/kpi/waterfall/unique-scheme/?${params}`),
        fetch(`https://api-project360-insights.haqdarshak.com/kpi/waterfall/unique-scheme-type/?${params}`),
        fetch(`https://api-project360-insights.haqdarshak.com/kpi/waterfall/unique-case-districts/?${params}`),
        fetch(`https://api-project360-insights.haqdarshak.com/kpi/waterfall/unique-thematic-areas/?${params}`)
      ]);

      const orgData = await orgRes.json();
      const schemeData = await schemeRes.json();
      const schemeTypeData = await schemeTypeRes.json();
      const caseDistrictData=await caseDistrictRes.json();
      const thematicData = await thematicRes.json();

      const orgOpts = orgData.unique_Organizations.map(o => ({ value: o, label: o }));
      const schemeOpts = schemeData.unique_Schemes.map(s => ({ value: s, label: s }));
      const caseDistrictOpts = caseDistrictData.unique_case_districts.map(c => ({ value: c, label: c }));
      const thematicOpts = thematicData.unique_thematic_areas.map(t => ({
        value: t,
        label: t
      }));
      const schemeTypeOpts = schemeTypeData.unique_Scheme_Types.map(t => ({
        value: t,
        label: schemeTypeLabelMap[t] || t
      }));

      setOrgOptions(mergeOptions(orgOptions, orgOpts));
      setSchemeOptions(mergeOptions(schemeOptions, schemeOpts));
      setSchemeTypeOptions(mergeOptions(schemeTypeOptions, schemeTypeOpts));
      setCaseDistrictOptions(mergeOptions(caseDistrictOptions,caseDistrictOpts));
      setThematicAreaOptions(mergeOptions(thematicAreaOptions,thematicOpts));

    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  useEffect(() => {
    fetchFilterOptions();
  }, [selectedOrgs, selectedSchemes, selectedSchemeTypes,selectedCaseDistrict,selectedThematicAreas,pid]);

  useEffect(() => {
    const fetchChartData = async () => {
      setLoading(true);
      try {
        const params = getQueryParams();

        const chartRes = await fetch(`https://api-project360-insights.haqdarshak.com/kpi/waterfall/chart/?${params}`);
        const chartData = await chartRes.json();

        const transformedData = [
          { name: 'Total Applications Opened', value: chartData.TotalCases, fill: '#CF87C4' },
          { prefix: 'Net', name: 'Applications Opened', value: chartData.application_open, fill: '#CF87C4' },
          { prefix: 'Net', name: 'Applications Submitted', value: chartData.docket_submitted, fill: '#CF87C4' },
          { prefix: 'Net', name: 'Applications with Benefit Received', value: chartData.br_count, fill: '#CF87C4' },
        ];

        const tableData = [
          ...transformedData,
          { name: 'Applications Aborted', value: chartData.aborted_count, fill: '#CF87C4' },
          { name: 'Applications Rejected', value: chartData.rejected_count, fill: '#CF87C4' } 
        ];

        const metricData = [
          { name: 'Applications Opened', value: chartData.application_open, fill: '#CF87C4' },
          { name: 'Applications Submitted', value: chartData.docket_submitted, fill: '#CF87C4' },
          { name: 'Applications with Benefit Received', value: chartData.br_count, fill: '#CF87C4' }
        ];

        setWaterfallData(metricData);
        setMetricsData(tableData);

        const benefitRes = await fetch(`https://api-project360-insights.haqdarshak.com/kpi/scheme-benefits/?${params}`);
        const benefitData = await benefitRes.json();

        setSchemeBenefit(benefitData.br_value || 0);

      } catch (error) {
        console.error('Error fetching chart/benefit data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchChartData();
  }, [selectedOrgs, selectedSchemes, selectedSchemeTypes,selectedCaseDistrict,selectedThematicAreas,pid]);

  // Info Button Component
  const InfoButton = ({ title }) => {
    const definition = definitions[title];
    if (!definition) return null;

    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          setInfoPopup({
            show: true,
            title: definition.title,
            description: definition.description,
            example: definition.example || ''
          });
        }}
        className="group relative ml-1.5 inline-flex items-center justify-center w-4 h-4 flex-shrink-0 rounded-full text-gray-400 transition-colors duration-300 ease-out hover:text-indigo-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-indigo-400"
        aria-label={`Info about ${title}`}
      >
        <span className="absolute inset-0 -m-2 rounded-full bg-white/0 ring-1 ring-transparent transition-all duration-300 ease-out group-hover:bg-white/70 group-hover:backdrop-blur-md group-hover:ring-black/5 group-hover:shadow-[0_4px_14px_rgba(99,102,241,0.18)]" />

        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="relative w-3.5 h-3.5 transition-transform duration-300 ease-out group-hover:scale-110"
        >
          <circle cx="12" cy="12" r="9.5" />
          <path d="M12 16v-4.5" />
          <path d="M12 7.8h.01" />
        </svg>
      </button>
    );
  };

  const MultiSelectDropdown = ({ options, selected, setSelected, placeholder, label }) => (
    <div className="relative">
      <div className="flex items-center mb-1">
        <label className="text-sm font-medium">{label}</label>
        <InfoButton title={label} />
      </div>
      <Select
        isMulti
        options={options}
        value={options.filter(opt => selected.includes(opt.value))}
        onChange={(selectedOptions) => setSelected(selectedOptions.map(o => o.value))}
        placeholder={placeholder}
        className="w-full"
        closeMenuOnSelect={false}
      />
    </div>
  );

  const currentOpen = Math.max(
    (metricsData.find(m => m.name === "Applications Opened")?.value || 0) -
    (metricsData.find(m => m.name === "Applications Submitted")?.value || 0), 
    0
  );

  const currentSubmitted = Math.max(
    (metricsData.find(m => m.name === "Applications Submitted")?.value || 0) -
    (metricsData.find(m => m.name === "Applications with Benefit Received")?.value || 0), 
    0
  );

  const currentBenefitReceived = metricsData.find(m => m.name === "Applications with Benefit Received")?.value || 0;

  const clearFilters = () => {
    setSelectedOrgs([]);
    setSelectedSchemes([]);
    setSelectedSchemeTypes([]);
    setSelectedCaseDistrict([]);
    setSelectedThematicAreas([]);
  };

  // Close info popup
  const closeInfoPopup = () => {
    setInfoPopup({ show: false, title: '', description: '', example: '' });
  };

  // Professional Info Popup Component
  const InfoPopup = () => {
    if (!infoPopup.show) return null;
    
    return (
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[100]"
        onClick={closeInfoPopup}
      >
        <div 
          className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-0 overflow-hidden animate-scaleIn"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header with gradient */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 18a9 9 0 100-18 9 9 0 000 18z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-white">{infoPopup.title}</h3>
            </div>
            <button
              onClick={closeInfoPopup}
              className="text-white/80 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-5">
            <div className="flex items-start gap-3 mb-4">
              <div className="mt-0.5">
                <svg className="w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-gray-700 leading-relaxed">{infoPopup.description}</p>
            </div>

            {infoPopup.example && (
              <div className="mt-4 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">📊</span>
                  <p className="text-sm font-semibold text-gray-800">Example</p>
                </div>
                <p className="text-sm text-gray-700 whitespace-pre-line font-mono bg-white/50 p-3 rounded-lg border border-blue-100">
                  {infoPopup.example}
                </p>
              </div>
            )}

            {/* Action buttons */}
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={closeInfoPopup}
                className="px-5 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-md hover:shadow-lg text-sm font-medium"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="w-[90vw] ml-[3vw] border-2 border-gray-300 shadow-md rounded-md">

      {/* HEADER */}
      <div className="bg-gray-50 px-4 md:px-6 py-4 border rounded-lg mb-4">
        <h2 className="text-xl md:text-xl font-bold text-pink-800">
          Summary View
        </h2>
        <p className="text-sm text-pink-800 mt-1">
          Filter and analyze your data
        </p>
      </div>

      {/* MAIN LAYOUT */}
      <div className="flex flex-col lg:flex-row gap-4">

        {/* LEFT METRIC CARDS */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-1 gap-3 w-full lg:w-64">
          {metricsData.map((item, index) => (
            <div
              key={index}
              className="bg-white p-4 rounded-lg shadow border relative"
            >
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-500">{item.name}</p>
                <InfoButton title={item.name} />
              </div>
              <p className="text-xl md:text-2xl font-bold mt-1" style={{ color: item.fill }}>
                {item.value?.toLocaleString()}
              </p>
            </div>
          ))}
        </div>

        {/* RIGHT CONTENT */}
        <div className="flex-1">

          {/* FILTERS */}
          <div className="bg-white rounded-lg shadow p-4 md:p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">

            <div>
              <MultiSelectDropdown {...{
                options: orgOptions,
                selected: selectedOrgs,
                setSelected: setSelectedOrgs,
                placeholder: "Select org/workspace",
                label: "Org/Workspace"
              }} />
            </div>

            <div>
              <MultiSelectDropdown {...{
                options: schemeTypeOptions,
                selected: selectedSchemeTypes,
                setSelected: setSelectedSchemeTypes,
                placeholder: "Select service type...",
                label: "Service Type"
              }} />
            </div>

            <div>
              <MultiSelectDropdown {...{
                options: schemeOptions,
                selected: selectedSchemes,
                setSelected: setSelectedSchemes,
                placeholder: "Select service name ...",
                label: "Service Name"
              }} />
            </div>

            <div>
              <MultiSelectDropdown {...{
                options: caseDistrictOptions,
                selected: selectedCaseDistrict,
                setSelected: setSelectedCaseDistrict,
                placeholder: "Select district...",
                label: "Service District"
              }} />
            </div>

            <div>
              <MultiSelectDropdown {...{
                options: thematicAreaOptions,
                selected: selectedThematicAreas,
                setSelected: setSelectedThematicAreas,
                placeholder: "Select service category...",
                label: "Service Category"
              }} />
            </div>

            <div className="flex items-end">
              <button
                onClick={clearFilters}
                className="bg-gray-200 hover:bg-gray-300 transition disabled:opacity-50 px-4 py-2 rounded w-full"
              >
                Clear Filters
              </button>
            </div>
          </div>

          {/* CHART + BENEFIT */}
          <div className="flex flex-col lg:flex-row gap-4 mt-4">

            {/* CHART */}
            <div className="flex-1 bg-white p-4 md:p-6 rounded-lg shadow border border-gray-200">
              {loading ? (
                <div className="flex justify-center h-40 items-center">
                  <div className="animate-spin h-10 w-10 border-2 border-blue-500 rounded-full"></div>
                </div>
              ) : waterfallData.length > 0 ? (
                <div className="flex flex-col items-center">
                  <div className="w-full h-44 flex flex-col justify-end space-y-1 relative">
                    <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gray-300 transform -translate-x-1/2"></div>

                    {(() => {
                      const max = Math.max(...waterfallData.map((item) => item.value), 1);
                      const appOpen = metricsData.find(m => m.name === "Applications Opened")?.value || 1;

                      return waterfallData.map((item) => {
                        let pct = 0;
                        if (item.name === "Applications Opened") {
                          pct = Math.round((item.value / appOpen) * 100);
                        } 
                        else if (item.name === "Applications Submitted") {
                          pct = Math.round((item.value / appOpen) * 100);
                        } 
                        else if (item.name === "Applications with Benefit Received") {
                          pct = Math.round((item.value / appOpen) * 100);
                        }

                        if (!isFinite(pct)) pct = 0;

                        const proportionalWidth = (item.value / max) * 25;
                        const halfWidth = Math.max(proportionalWidth, 7.5);

                        return (
                          <div key={item.name} className="flex justify-center relative">
                            <div
                              className="flex items-center justify-center h-10 rounded-sm"
                              style={{
                                left: `calc(50% - ${halfWidth}vw)`,
                                width: `${halfWidth * 2}vw`,
                                minWidth: '15vw',
                                backgroundColor: item.fill,
                              }}
                            >
                              <span className="text-sm md:text-md font-semibold text-white whitespace-nowrap">
                                {item.name}: {item.value.toLocaleString()} ({pct}%)
                              </span>
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              ) : (
                <div className="text-center py-10 text-gray-500">
                  Select filters to view the workflow data
                </div>
              )}
            </div>

            {/* BENEFIT */}
            <div className="w-full lg:w-72 bg-white p-6 rounded-lg shadow flex flex-col items-center justify-center relative">
              <div className="flex items-center">
                <h2 className="text-lg font-bold text-purple-800">Benefits unlocked worth</h2>
                <InfoButton title="Benefits unlocked worth" />
              </div>
              <p className="text-2xl md:text-3xl font-bold text-purple-800 mt-2">
                ₹{schemeBenefit.toLocaleString('en-IN')}
              </p>
            </div>

          </div>

          {/* SNAPSHOT VIEW */}
          <div className="mt-6">
            <div className="flex items-center mb-4">
              <h2 className="text-lg md:text-xl font-semibold text-pink-800">
                Current snapshot
              </h2>
              <InfoButton title="Snapshot View" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">

              <div className="bg-pink-100 p-5 rounded-xl text-center shadow relative">
                <div className="flex items-center justify-center">
                  <h3 className="text-pink-800 font-semibold">Open Applications</h3>
                  <InfoButton title="Current Open" />
                </div>
                <p className="text-2xl md:text-3xl font-bold mt-2">
                  {currentOpen.toLocaleString()}
                </p>
              </div>

              <div className="bg-purple-100 p-5 rounded-xl text-center shadow relative">
                <div className="flex items-center justify-center">
                  <h3 className="text-purple-800 font-semibold">Applications in Submitted status</h3>
                  <InfoButton title="Current Submitted" />
                </div>
                <p className="text-2xl md:text-3xl font-bold mt-2">
                  {currentSubmitted.toLocaleString()}
                </p>
              </div>

              <div className="bg-indigo-100 p-5 rounded-xl text-center shadow relative">
                <div className="flex items-center justify-center">
                  <h3 className="text-indigo-800 font-semibold">Applications with Benefits Received</h3>
                  <InfoButton title="Current Benefit Received" />
                </div>
                <p className="text-2xl md:text-3xl font-bold mt-2">
                  {currentBenefitReceived.toLocaleString()}
                </p>
              </div>

            </div>
          </div>

        </div>
      </div>

      {/* Info Popup */}
      <InfoPopup />

      <style jsx>{`
        @keyframes scaleIn {
          from { 
            opacity: 0; 
            transform: scale(0.95) translateY(-10px); 
          }
          to { 
            opacity: 1; 
            transform: scale(1) translateY(0); 
          }
        }
        .animate-scaleIn {
          animation: scaleIn 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
      `}</style>
    </div>
  );
};

export default WaterfallChartComponent;